import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ListChecks, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'

type WorkflowStep = {
  n: number
  action: string
  agent: string
  escalation: 'green' | 'yellow' | 'red'
  notes?: string
}

type Workflow = {
  id: string
  code: string
  name: string
  description: string | null
  triggerDescription: string | null
  appliesTo: string[]
  steps: WorkflowStep[]
  active: boolean
  version: number
  updatedAt: string
  metadata: Record<string, unknown>
}

type ListResult = { results: Workflow[]; total: number }

const AGENT_OPTIONS = [
  'pm',
  'ba',
  'architect',
  'techlead',
  'backend',
  'frontend',
  'devops',
  'qa',
  'designer',
  'support',
  'sales',
  'finance',
  'legal',
  'marketing',
  'custom',
]

const ESC_LABELS: Record<WorkflowStep['escalation'], string> = {
  green: 'Verde — autonomo',
  yellow: 'Giallo — approva',
  red: 'Rosso — ferma',
}

const ESC_BADGE: Record<WorkflowStep['escalation'], 'success' | 'warning' | 'danger'> = {
  green: 'success',
  yellow: 'warning',
  red: 'danger',
}

function blankStep(n: number): WorkflowStep {
  return { n, action: '', agent: 'pm', escalation: 'green' }
}

function emptyWorkflow(): Workflow {
  return {
    id: '',
    code: '',
    name: '',
    description: '',
    triggerDescription: '',
    appliesTo: ['all'],
    steps: [blankStep(1)],
    active: true,
    version: 0,
    updatedAt: new Date().toISOString(),
    metadata: {},
  }
}

export function WorkflowsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Workflow | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => apiClient.get<ListResult>('/workflows?limit=200'),
  })

  return (
    <AppLayout>
      <PageHeader
        title="Workflow"
        description={data ? `${data.total} workflow definiti` : 'SOP e procedure editabili'}
        action={
          <Button onClick={() => setEditing(emptyWorkflow())}>
            <Plus className="size-4" /> Nuovo workflow
          </Button>
        }
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
            <Card key={i}>
              <CardBody>
                <Skeleton className="h-5 w-1/3 mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : data && data.results.length > 0 ? (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium w-20">Codice</th>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Trigger</th>
                <th className="text-left px-5 py-3 font-medium w-20 text-center">Step</th>
                <th className="text-left px-5 py-3 font-medium w-24">Stato</th>
                <th className="text-left px-5 py-3 font-medium w-16 text-right">Ver</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((w) => (
                <tr
                  key={w.id}
                  onClick={() => setEditing(w)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setEditing(w)
                  }}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 cursor-pointer transition"
                >
                  <td className="px-5 py-3">
                    <Badge variant="info">{w.code}</Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-100">{w.name}</div>
                    {w.description && (
                      <div className="text-xs text-zinc-500 line-clamp-1">{w.description}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-zinc-400 text-xs line-clamp-2">
                    {w.triggerDescription ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-center tabular-nums text-zinc-300">
                    {w.steps.length}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={w.active ? 'success' : 'muted'}>
                      {w.active ? 'attivo' : 'inattivo'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-zinc-500 tabular-nums">
                    v{w.version}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Empty
              icon={ListChecks}
              title="Nessun workflow"
              description="I workflow sono SOP e procedure ripetibili. Il seed iniziale viene popolato da claude-config/core/sop/. Crea il primo manualmente o esegui scripts/seed-workflows.mjs."
              action={
                <Button onClick={() => setEditing(emptyWorkflow())}>
                  <Plus className="size-4" /> Nuovo workflow
                </Button>
              }
            />
          </CardBody>
        </Card>
      )}

      {editing && (
        <WorkflowEditor
          workflow={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['workflows'] })
            setEditing(null)
          }}
        />
      )}
    </AppLayout>
  )
}

// ============================================================
// Editor dialog
// ============================================================

function WorkflowEditor({
  workflow,
  onClose,
  onSaved,
}: {
  workflow: Workflow
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !workflow.id
  const [code, setCode] = useState(workflow.code)
  const [name, setName] = useState(workflow.name)
  const [description, setDescription] = useState(workflow.description ?? '')
  const [triggerDescription, setTriggerDescription] = useState(workflow.triggerDescription ?? '')
  const [appliesTo, setAppliesTo] = useState((workflow.appliesTo ?? ['all']).join(', '))
  const [active, setActive] = useState(workflow.active)
  const [steps, setSteps] = useState<WorkflowStep[]>(workflow.steps ?? [blankStep(1)])

  useEffect(() => {
    setCode(workflow.code)
    setName(workflow.name)
    setDescription(workflow.description ?? '')
    setTriggerDescription(workflow.triggerDescription ?? '')
    setAppliesTo((workflow.appliesTo ?? ['all']).join(', '))
    setActive(workflow.active)
    setSteps(workflow.steps ?? [blankStep(1)])
  }, [workflow])

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      isNew
        ? apiClient.post('/workflows', body)
        : apiClient.patch(`/workflows/${workflow.id}`, body),
    onSuccess: () => onSaved(),
  })

  function reindex(arr: WorkflowStep[]) {
    return arr.map((s, i) => ({ ...s, n: i + 1 }))
  }

  function addStep() {
    setSteps((s) => reindex([...s, blankStep(s.length + 1)]))
  }

  function removeStep(idx: number) {
    setSteps((s) => reindex(s.filter((_, i) => i !== idx)))
  }

  function moveStep(idx: number, dir: -1 | 1) {
    setSteps((s) => {
      const target = idx + dir
      if (target < 0 || target >= s.length) return s
      const a = s[idx]
      const b = s[target]
      if (!a || !b) return s
      const copy = [...s]
      copy[idx] = b
      copy[target] = a
      return reindex(copy)
    })
  }

  function updateStep(idx: number, patch: Partial<WorkflowStep>) {
    setSteps((s) => s.map((step, i) => (i === idx ? { ...step, ...patch } : step)))
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const body = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
      triggerDescription: triggerDescription.trim() || null,
      appliesTo: appliesTo
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      steps: reindex(steps.filter((s) => s.action.trim())).map((s) => ({
        ...s,
        action: s.action.trim(),
        notes: s.notes?.trim() || undefined,
      })),
      active,
    }
    save.mutate(body)
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'Nuovo workflow' : `${workflow.code} — ${workflow.name}`}
      description={
        isNew
          ? 'Definisci codice, nome, trigger e gli step della procedura.'
          : `Versione corrente: v${workflow.version}. Ogni modifica incrementa la versione.`
      }
      size="xl"
    >
      <form onSubmit={onSubmit} className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-2">
            <Label htmlFor="code">Codice *</Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              placeholder="P1"
              required
              maxLength={32}
            />
          </div>
          <div className="col-span-7">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="Onboarding Cliente"
              required
              maxLength={200}
            />
          </div>
          <div className="col-span-3">
            <Label htmlFor="active">Stato</Label>
            <Select
              id="active"
              value={active ? 'true' : 'false'}
              onChange={(e) => setActive(e.currentTarget.value === 'true')}
            >
              <option value="true">Attivo</option>
              <option value="false">Inattivo</option>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="trigger">Trigger</Label>
          <Input
            id="trigger"
            value={triggerDescription}
            onChange={(e) => setTriggerDescription(e.currentTarget.value)}
            placeholder="Contratto firmato (fine SOP-C2)"
          />
        </div>

        <div>
          <Label htmlFor="description">Descrizione</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder="A cosa serve questo workflow…"
          />
        </div>

        <div>
          <Label htmlFor="appliesTo">Applies to (tag separati da virgola)</Label>
          <Input
            id="appliesTo"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.currentTarget.value)}
            placeholder="all, software_dev, consulting"
          />
        </div>

        {/* Steps */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Step</Label>
            <Button type="button" variant="ghost" size="sm" onClick={addStep}>
              <Plus className="size-3.5" /> Aggiungi step
            </Button>
          </div>
          <div className="space-y-2">
            {steps.map((s, idx) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: editable workflow step list, reorders re-render the entire row anyway
                key={idx}
                className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 grid grid-cols-12 gap-2 items-start"
              >
                <div className="col-span-1 flex flex-col items-center gap-1 pt-1.5">
                  <span className="text-xs font-semibold text-zinc-400 tabular-nums">{s.n}</span>
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveStep(idx, -1)}
                      disabled={idx === 0}
                      className="p-0.5 rounded hover:bg-zinc-800 text-zinc-500 disabled:opacity-30"
                      aria-label="Sposta su"
                    >
                      <ArrowUp className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(idx, 1)}
                      disabled={idx === steps.length - 1}
                      className="p-0.5 rounded hover:bg-zinc-800 text-zinc-500 disabled:opacity-30"
                      aria-label="Sposta giù"
                    >
                      <ArrowDown className="size-3" />
                    </button>
                  </div>
                </div>
                <div className="col-span-6">
                  <Textarea
                    placeholder="Azione (es. Email di benvenuto al cliente)"
                    value={s.action}
                    onChange={(e) => updateStep(idx, { action: e.currentTarget.value })}
                    className="min-h-12"
                    rows={2}
                  />
                  {s.notes !== undefined || s.escalation !== 'green' ? (
                    <Input
                      placeholder="Note (opzionale)"
                      value={s.notes ?? ''}
                      onChange={(e) => updateStep(idx, { notes: e.currentTarget.value })}
                      className="mt-1 text-xs"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => updateStep(idx, { notes: '' })}
                      className="mt-1 text-xs text-zinc-500 hover:text-zinc-300"
                    >
                      + aggiungi nota
                    </button>
                  )}
                </div>
                <div className="col-span-2">
                  <Select
                    value={AGENT_OPTIONS.includes(s.agent) ? s.agent : 'custom'}
                    onChange={(e) => {
                      const val = e.currentTarget.value
                      updateStep(idx, { agent: val === 'custom' ? s.agent : val })
                    }}
                  >
                    {AGENT_OPTIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </Select>
                  {!AGENT_OPTIONS.slice(0, -1).includes(s.agent) && (
                    <Input
                      placeholder="agent custom"
                      value={s.agent}
                      onChange={(e) => updateStep(idx, { agent: e.currentTarget.value })}
                      className="mt-1 text-xs"
                    />
                  )}
                </div>
                <div className="col-span-2">
                  <Select
                    value={s.escalation}
                    onChange={(e) =>
                      updateStep(idx, {
                        escalation: e.currentTarget.value as WorkflowStep['escalation'],
                      })
                    }
                  >
                    <option value="green">Verde</option>
                    <option value="yellow">Giallo</option>
                    <option value="red">Rosso</option>
                  </Select>
                  <Badge
                    variant={ESC_BADGE[s.escalation]}
                    className="mt-1 block text-center text-[10px]"
                  >
                    {ESC_LABELS[s.escalation]}
                  </Badge>
                </div>
                <div className="col-span-1 flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => removeStep(idx)}
                    disabled={steps.length === 1}
                    className="p-1.5 rounded hover:bg-red-900/30 text-zinc-500 hover:text-red-400 disabled:opacity-30"
                    aria-label="Rimuovi step"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {save.isError && (
          <div className="text-sm text-red-400">
            {(save.error as Error)?.message ?? 'Errore salvataggio'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800/60 sticky bottom-0 bg-zinc-950/95 -mx-5 px-5 -mb-5 pb-5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Salvataggio…' : isNew ? 'Crea workflow' : 'Salva modifiche'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
